import { get } from 'lodash';
import React, { useState, useMemo, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useClient } from 'urql';
import { useAppSelector, useAppDispatch } from '../../app/hooks'
import { AvoRedApp } from '../../components/Layout/AvoRedApp'
import { visitorId, setCartItemsCount } from '../../features/cart/cartSlice'
import { REMOVE_FROM_CART_MUTATION } from '../../graphql/mutations/removeFromCartMutation'
import { startLoading, stopLoading } from '../../features/loading/loadingSlice'
import ImageWithFallback from '../../components/ImageWithFallback';
import { CUSTOMER_QUERY } from '../../graphql/queries/categoryQuery';
import { isAuth } from '../../features/userLogin/userLoginSlice';
import { Toast } from '../../components/Toast';

const GetCartItemsCount = `
    query CartItems($visitorId: String!)  {
      cartItems (visitor_id: $visitorId)  {
          items {
              id
          }
          children {
              items {
                  id
              }
              children {
                  items {
                      id
                  }
              }
          }
      }
    }
`;

const GetCartItems = `
query CartItems($visitorId: String!)  {
  cartItems (visitor_id: $visitorId)  {
      category_id
      category_name
      category {
          id
          name
          slug
      }
      items {
          id
          visitor_id
          product_id
          variant_id
          qty
          product {
              id
              name
              slug
              main_image_url
              price
              final_price
              min_order_qty
              delivery_date
          }
          variant {
              id
              name
              sku
              price
              final_price
          }
      }
      children {
          category_id
          category_name
          category {
              id
              name
              slug
          }
          items {
              id
              visitor_id
              product_id
              variant_id
              qty
              product {
                  id
                  name
                  slug
                  main_image_url
                  price
                  final_price
                  min_order_qty
                  delivery_date
              }
              variant {
                  id
                  name
                  sku
                  price
                  final_price
              }
          }
          children {
              category_id
              category_name
              category {
                  id
                  name
                  slug
              }
              items {
                  id
                  visitor_id
                  product_id
                  variant_id
                  qty
                  product {
                      id
                      name
                      slug
                      main_image_url
                      price
                      final_price
                      min_order_qty
                      delivery_date
                  }
                  variant {
                      id
                      name
                      sku
                      price
                      final_price
                  }
              }
          }
      }
  }
}
`;

const BRANCHES_QUERY = `
  query Branches { branches { id name address } }
`;

const UPDATE_QTY_MUTATION = `
  mutation UpdateQty($visitorId: String!, $slug: String!, $qty: Float!) {
    updateCartItemQty(visitor_id: $visitorId, slug: $slug, qty: $qty) {
      total
      branch { id name address }
      items { id name image price qty total slug }
    }
  }
`;

const SELECT_BRANCH_MUTATION = `
  mutation SelectBranch($branchId: ID!) { 
    selectBranch(branch_id: $branchId) { id name address } 
  }
`;

const CREATE_ORDER_MUTATION = `
  mutation CreateOrder($branchId: ID!, $categoryId: ID, $items: [OrderItemInput!]!) {
    createOrder(branchId: $branchId, categoryId: $categoryId, items: $items) {
      id
      track_code
      status
      total
      branch {
        id
        name
        address
      }
      orderItems {
        id
        product_id
        variant_id
        quantity
        price
        price_before_tax
        vat
        subtotal
      }
    }
  }
`;


export const CartShow = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isUserLoggedIn = useAppSelector(isAuth);

  const currentVisitorId = useAppSelector(visitorId)
  const client = useClient();

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isUserLoggedIn) {
      navigate('/login');
    }
  }, [isUserLoggedIn, navigate]);

  // Format price without rounding
  const formatPrice = (price: number | string): string => {
    // Convert to number if string
    const numPrice = typeof price === 'string' ? parseFloat(price) : Number(price);
    if (isNaN(numPrice) || numPrice <= 0) return '0';
    
    // Round to 2 decimal places to avoid floating point precision issues
    const roundedPrice = Math.round(numPrice * 100) / 100;
    
    // Split into integer and decimal parts
    const parts = roundedPrice.toString().split('.');
    const integerPart = parts[0];
    const decimalPart = parts[1] || '';
    
    // Format only the integer part with thousand separators
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    
    // Combine: integer part with separators + decimal part (if exists and not .00, limit to 2 digits)
    if (decimalPart) {
      const limitedDecimal = decimalPart.substring(0, 2).padEnd(2, '0');
      // Only add decimal part if it's not .00
      if (limitedDecimal === '00') {
        return formattedInteger;
      }
      return `${formattedInteger},${limitedDecimal}`;
    }
    return formattedInteger;
  };

  const getItemDeliveryDate = (cartItem: any): number => {
    const productDeliveryDate = Number(get(cartItem, 'product.delivery_date', 0)) || 0;
    const variantDeliveryDate = Number(get(cartItem, 'variant.delivery_date', 0)) || 0;
    return productDeliveryDate || variantDeliveryDate || 7;
  };

  const getGroupMaxDeliveryDate = (items: any[]): number => {
    return items.reduce((max: number, cartItem: any) => {
      return Math.max(max, getItemDeliveryDate(cartItem));
    }, 0);
  };

  const getDeliveryDateText = (deliveryDate: number): string => {
    return deliveryDate > 0 ? `${deliveryDate} ngày kể từ khi đặt hàng` : 'N/A';
  };

  const [{ fetching, data }, reexecuteQuery] = useQuery({ 
    query: GetCartItems, 
    variables: { visitorId: currentVisitorId },
    requestPolicy: 'cache-and-network',
    pause: !isUserLoggedIn || !currentVisitorId
  });
  const [{ data: branchesData }] = useQuery({ query: BRANCHES_QUERY });
  const [{ data: customerData }] = useQuery({ query: CUSTOMER_QUERY });
  const [, removeFromCart] = useMutation(REMOVE_FROM_CART_MUTATION);
  const [, updateQty] = useMutation(UPDATE_QTY_MUTATION);
  const [, selectBranch] = useMutation(SELECT_BRANCH_MUTATION);
  const [, createOrder] = useMutation(CREATE_ORDER_MUTATION);

  const [qtyDraft, setQtyDraft] = useState<{ [key: string]: string }>({});
  const [selectedBranchId, setSelectedBranchId] = useState(() => localStorage.getItem('selected_branch_id') || '');
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [placedOrders, setPlacedOrders] = useState<{ [key: string]: any }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState<{
    isVisible: boolean;
    message: string;
    type: "success" | "error";
  }>({
    isVisible: false,
    message: "",
    type: "success",
  });

  const branches = branchesData?.branches || [];
  const customerGroup = customerData?.customerQuery?.group;
  const customer = customerData?.customerQuery;
  const selectedBranch = branches.find((b: any) => b.id === selectedBranchId);

  useEffect(() => {
  }, [data]);

  const showToast = (success: boolean, message: string) => {
    setToast({
      isVisible: true,
      message,
      type: success ? "success" : "error",
    });
  };

  const handleCloseToast = () => {
    setToast((prev) => ({ ...prev, isVisible: false }));
  };

  const handleQtyChange = (slug: string, value: string, minOrderQty: number = 1) => {
    setQtyDraft(prev => ({ ...prev, [slug]: value }));
    const qtyValue = parseFloat(value);
    if (Number.isNaN(qtyValue) || qtyValue <= 0) return;
    if (qtyValue < minOrderQty) {
      showToast(false, `Vui lòng chọn tối thiểu ${minOrderQty} sản phẩm`);
      setQtyDraft(prev => ({ ...prev, [slug]: minOrderQty.toString() }));
      return;
    }
    handleUpdateQty(slug, qtyValue);
  };

  const handleUpdateQty = async (slug: string, qtyValue: number) => {
    if (!currentVisitorId) return;
    
    dispatch(startLoading());
    try {
      const result = await updateQty({ visitorId: currentVisitorId, slug, qty: qtyValue });
      
      if (result.error) {
        console.error('UpdateQty error:', result.error);
        dispatch(stopLoading());
        return;
      }
      
      // Lấy qty từ response và set lại vào input
      const updatedItem = result.data?.updateCartItemQty?.items?.find((item: any) => item.slug === slug);
      if (updatedItem && updatedItem.qty != null) {
        setQtyDraft(prev => ({ ...prev, [slug]: updatedItem.qty.toString() }));
      } else {
        // Fallback: nếu không tìm thấy trong response, giữ nguyên giá trị đã nhập
        setQtyDraft(prev => ({ ...prev, [slug]: qtyValue.toString() }));
      }
      
      reexecuteQuery({ requestPolicy: 'network-only' });
    } catch (error) {
      console.error('UpdateQty exception:', error);
    } finally {
      dispatch(stopLoading());
    }
  };

  const handleSelectBranch = async (branchId: string) => {
    if (!branchId || branchId.trim() === '') {
      // Don't call mutation if branchId is empty
      setSelectedBranchId('');
      localStorage.removeItem('selected_branch_id');
      return;
    }
    
    setSelectedBranchId(branchId);
    await selectBranch({ branchId });
    localStorage.setItem('selected_branch_id', branchId);
    reexecuteQuery({ requestPolicy: 'network-only' });
  };

  const handleRemove = async (productId: string, variantId?: string) => {
    if (!currentVisitorId) return;
    const variables: any = { visitor_id: currentVisitorId, product_id: productId };
    if (variantId) variables.variant_id = variantId;
    dispatch(startLoading());
    await removeFromCart(variables);
    dispatch(stopLoading());
    // Giữ lại data cũ trong lúc fetch data mới
    reexecuteQuery({ requestPolicy: 'cache-and-network' });
      // Update cart count from query result
      if (currentVisitorId) {
        const result = await client.query(GetCartItemsCount, { visitorId: currentVisitorId }, { requestPolicy: 'network-only' }).toPromise();
        // Helper function để đếm tất cả items trong cây category
        const countItems = (groups: any[]): number => {
          let count = 0;
          groups.forEach((group: any) => {
            if (group.items) count += group.items.length;
            if (group.children) count += countItems(group.children);
          });
          return count;
        };
        const count = countItems(get(result.data, 'cartItems', []));
        dispatch(setCartItemsCount(count));
      }
  }

  const handlePlaceSingleOrder = async (categoryId: string) => {
    if (!selectedBranchId) {
      alert('Vui lòng chọn chi nhánh trước khi đặt hàng');
      return;
    }

    const group = groupedCartItems.find((g: any) => g.categoryId === categoryId);
    if (!group) return;

    setIsLoading(true);
    dispatch(startLoading());
    try {
      const items = group.items.map((cartItem: any) => {
        const qtyRaw = get(cartItem, 'qty', 1);
        const qty = typeof qtyRaw === 'string' ? parseFloat(qtyRaw) : Number(qtyRaw);
        
        return {
          product_id: get(cartItem, 'product.id'),
          variant_id: get(cartItem, 'variant.id') || null,
          qty: (!isNaN(qty) && qty >= 0) ? qty : 1,
        };
      });

      const result = await createOrder({
        branchId: selectedBranchId,
        categoryId: group.categoryId !== 'no-category' ? group.categoryId : null,
        items: items,
      });

      dispatch(stopLoading());
      setIsLoading(false);

      if (result.data?.createOrder) {
        const order = result.data.createOrder;
        setPlacedOrders((prev) => ({
          ...prev,
          [categoryId]: order,
        }));

        const maxDeliveryDateInGroup = getGroupMaxDeliveryDate(group.items);

        // Refresh cart để cập nhật danh sách
        reexecuteQuery({ requestPolicy: 'network-only' });
        
        // Update cart count
        if (currentVisitorId) {
          const result = await client.query(GetCartItemsCount, { visitorId: currentVisitorId }, { requestPolicy: 'network-only' }).toPromise();
          const countItems = (groups: any[]): number => {
            let count = 0;
            groups.forEach((group: any) => {
              if (group.items) count += group.items.length;
              if (group.children) count += countItems(group.children);
            });
            return count;
          };
          const count = countItems(get(result.data, 'cartItems', []));
          dispatch(setCartItemsCount(count));
        }

        const orderSuccessMessage = [
          'Đặt hàng thành công!',
          '',
          `Đơn hàng: ${group.categoryName}`,
          `Mã đơn: ${order.track_code || order.id}`,
          `Tổng tiền: ${formatPrice(order.total || 0)} đ`,
          '',
          `Chi nhánh: ${selectedBranch?.name || 'N/A'}`,
          '',
          `Ngày giao hàng dự kiến: ${getDeliveryDateText(maxDeliveryDateInGroup)}`,
        ].join('\n');

        alert(orderSuccessMessage);
      } else {
        alert('Có lỗi xảy ra khi đặt hàng. Vui lòng thử lại.');
      }
    } catch (error) {
      dispatch(stopLoading());
      setIsLoading(false);
      console.error('Error placing order:', error);
      alert('Có lỗi xảy ra khi đặt hàng. Vui lòng thử lại.');
    }
  }

  const handlePlaceAllOrders = async () => {
    if (!selectedBranchId || !groupedCartItems.length) return;
    
    setIsLoading(true);
    dispatch(startLoading());
    try {
      // Tạo order cho mỗi category chưa được đặt
      const orders: any[] = [];
      const orderDeliveryDates: number[] = [];
      for (const group of groupedCartItems) {
        // Bỏ qua các đơn hàng đã được đặt
        if (placedOrders[group.categoryId]) {
          orders.push(placedOrders[group.categoryId]);
          orderDeliveryDates.push(group.maxDeliveryDate || 0);
          continue;
        }

        const items = group.items.map((cartItem: any) => {
          const variantPrice = get(cartItem, 'variant.final_price') || get(cartItem, 'variant.price');
          const productPrice = get(cartItem, 'product.final_price') || get(cartItem, 'product.price');
          const unitPriceRaw = variantPrice || productPrice || 0;
          const unitPrice = typeof unitPriceRaw === 'string' ? parseFloat(unitPriceRaw) : Number(unitPriceRaw);
          const qtyRaw = get(cartItem, 'qty', 1);
          const qty = typeof qtyRaw === 'string' ? parseFloat(qtyRaw) : Number(qtyRaw);
          
          return {
            product_id: get(cartItem, 'product.id'),
            variant_id: get(cartItem, 'variant.id') || null,
            qty: (!isNaN(qty) && qty >= 0) ? qty : 1,
            price: (!isNaN(unitPrice) && unitPrice >= 0) ? unitPrice : 0,
          };
        });

        const result = await createOrder({
          branchId: selectedBranchId,
          categoryId: group.categoryId !== 'no-category' ? group.categoryId : null,
          items: items,
        });

        if (result.data?.createOrder) {
          const order = result.data.createOrder;
          orders.push(order);
          orderDeliveryDates.push(group.maxDeliveryDate || 0);
          setPlacedOrders((prev) => ({
            ...prev,
            [group.categoryId]: order,
          }));
        }
      }

      dispatch(stopLoading());
      setIsLoading(false);
      setShowOrderModal(false);
      
      // Hiển thị thông báo thành công với chi tiết từng đơn hàng
      const orderDetails = orders.map((order: any, idx: number) => {
        const category = groupedCartItems[idx];
        return `\n- Đơn ${idx + 1}: ${category?.categoryName || 'Không có danh mục'} (Mã: ${order.track_code || order.id}, Tổng: ${formatPrice(order.total || 0)} đ)`;
      }).join('');

      const maxDeliveryDateForAllOrders = orderDeliveryDates.length > 0
        ? Math.max(...orderDeliveryDates)
        : 0;
      
      alert(
        `Đặt hàng thành công!\n\nĐã tạo ${orders.length} đơn hàng với trạng thái pending:${orderDetails}\n\nChi nhánh: ${selectedBranch?.name || 'N/A'}\n\nNgày giao hàng dự kiến: ${getDeliveryDateText(maxDeliveryDateForAllOrders)}`
      );
      
      // Refresh cart
      reexecuteQuery({ requestPolicy: 'network-only' });
      
      // Update cart count
      if (currentVisitorId) {
        const result = await client.query(GetCartItemsCount, { visitorId: currentVisitorId }, { requestPolicy: 'network-only' }).toPromise();
        const count = get(result.data, 'cartItems', []).length;
        dispatch(setCartItemsCount(count));
      }
    } catch (error) {
      dispatch(stopLoading());
      setIsLoading(false);
      console.error('Error placing order:', error);
      alert('Có lỗi xảy ra khi đặt hàng. Vui lòng thử lại.');
    }
  }

  /**
   * Flatten category groups để hiển thị và tính tổng
   * Backend đã group theo category tree, frontend gom tất cả items từ children vào parent category
   */
  const { groupedCartItems, cartTotal } = useMemo(() => {
    const groups = get(data, 'cartItems', []);
    
    // Helper function để collect tất cả items từ group và children
    const collectAllItems = (group: any): any[] => {
      const items: any[] = [];
      // Add direct items
      if (group.items && group.items.length > 0) {
        items.push(...group.items);
      }
      // Recursively collect items from all children
      if (group.children && group.children.length > 0) {
        group.children.forEach((child: any) => {
          items.push(...collectAllItems(child));
        });
      }
      return items;
    };

    // Helper function để flatten category tree - gom items từ children vào parent
    const flattenGroups = (groups: any[]): any[] => {
      const result: any[] = [];
      
      groups.forEach((group: any) => {
        // Collect all items from this group and all its children
        const allItems = collectAllItems(group);
        
        // Only add to result if there are items
        if (allItems.length > 0) {
          result.push({
            categoryId: group.category_id || 'no-category',
            categoryName: group.category_name || 'Không có danh mục',
            items: allItems
          });
        }
      });
      
      return result;
    };
    
    const flattenedGroups = flattenGroups(groups);
    
    // Tính tổng tiền và ngày giao hàng dự kiến (lấy delivery_date lớn nhất)
    let total = 0;
    flattenedGroups.forEach((group: any) => {
      let maxDeliveryDate = 0;
      group.items.forEach((cartItem: any) => {
        const variantPrice = get(cartItem, 'variant.final_price') || get(cartItem, 'variant.price');
        const productPrice = get(cartItem, 'product.final_price') || get(cartItem, 'product.price');
        const unitPriceRaw = variantPrice || productPrice || 0;
        const unitPrice = typeof unitPriceRaw === 'string' ? parseFloat(unitPriceRaw) : Number(unitPriceRaw);
        const qtyRaw = get(cartItem, 'qty', 1);
        const qty = typeof qtyRaw === 'string' ? parseFloat(qtyRaw) : Number(qtyRaw);
        
        if (!isNaN(unitPrice) && !isNaN(qty) && unitPrice >= 0 && qty >= 0) {
          const itemTotal = unitPrice * qty;
          total = Math.round((total + itemTotal) * 100) / 100;
        }

         const productDeliveryDate = get(cartItem, 'product.delivery_date') ? Number(get(cartItem, 'product.delivery_date')) : 0;
         // Also check variant.delivery_date as fallback (some systems store it there)
         const variantDeliveryDate = get(cartItem, 'variant.delivery_date') ? Number(get(cartItem, 'variant.delivery_date')) : 0;
         const itemDeliveryDate = productDeliveryDate || variantDeliveryDate || 7; // fallback to 7 days if both are 0/missing
         if (itemDeliveryDate > maxDeliveryDate) {
           maxDeliveryDate = itemDeliveryDate;
         }
      });
      group.maxDeliveryDate = maxDeliveryDate;
    });
    
    return { groupedCartItems: flattenedGroups, cartTotal: total };
  }, [data]);

  // Kiểm tra xem có items không (cần check cả items và children)
  const isEmpty = useMemo(() => {
    const groups = get(data, 'cartItems', []);
    if (!groups || groups.length === 0) return true;
    
    // Kiểm tra xem có bất kỳ group nào có items không
    const hasItems = groups.some((group: any) => {
      const itemsCount = (group.items && group.items.length > 0) ? group.items.length : 0;
      const childrenHasItems = group.children && group.children.length > 0 
        ? group.children.some((child: any) => child.items && child.items.length > 0)
        : false;
      return itemsCount > 0 || childrenHasItems;
    });
    
    return !hasItems;
  }, [data]);

  // Calculate max delivery date for all cart items
  const maxDeliveryDate = useMemo(() => {
    let max = 0;
    groupedCartItems.forEach((group: any) => {
      if (group.maxDeliveryDate > max) {
        max = group.maxDeliveryDate;
      }
    });
    return max;
  }, [groupedCartItems]);

  // Skeleton Component
  const CartSkeleton = () => (
    <>
      {/* Header Skeleton */}
      <div className="mb-6">
        <div className="h-9 bg-gray-200 rounded-lg w-48 mb-2 animate-pulse"></div>
        <div className="h-5 bg-gray-200 rounded-lg w-64 animate-pulse"></div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Cart Items Skeleton */}
        <div className="lg:w-2/3 bg-white rounded-lg shadow-sm overflow-hidden">
          {/* Table Header Skeleton */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-5 h-4 bg-gray-200 rounded animate-pulse"></div>
              <div className="col-span-3 h-4 bg-gray-200 rounded animate-pulse"></div>
              <div className="col-span-2 h-4 bg-gray-200 rounded animate-pulse"></div>
              <div className="col-span-2 h-4 bg-gray-200 rounded animate-pulse"></div>
            </div>
          </div>

          {/* Cart Items Skeleton */}
          <div className="divide-y divide-gray-200">
            {/* Category Group Skeleton */}
            {[1, 2].map((category) => (
              <div key={category} className="py-4">
                {/* Category Header Skeleton */}
                <div className="px-6 mb-3">
                  <div className="h-6 bg-gray-200 rounded w-48 animate-pulse"></div>
                </div>
                
                {/* Items in Category Skeleton */}
                {[1, 2].map((item) => (
                  <div key={`${category}-${item}`} className="px-6 py-4">
                    <div className="grid grid-cols-12 gap-4 items-center">
                      {/* Product Info Skeleton */}
                      <div className="col-span-5 flex items-start space-x-4">
                        <div className="w-20 h-20 flex-shrink-0 rounded-lg bg-gray-200 animate-pulse"></div>
                        <div className="flex-1 space-y-2">
                          <div className="h-5 bg-gray-200 rounded w-3/4 animate-pulse"></div>
                          <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse"></div>
                          <div className="h-4 bg-gray-200 rounded w-16 animate-pulse"></div>
                        </div>
                      </div>
                      {/* Quantity Skeleton */}
                      <div className="col-span-3 flex justify-center">
                        <div className="flex items-center space-x-2">
                          <div className="w-20 h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                          <div className="w-10 h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                        </div>
                      </div>
                      {/* Price Skeletons */}
                      <div className="col-span-2">
                        <div className="h-5 bg-gray-200 rounded w-24 ml-auto animate-pulse"></div>
                      </div>
                      <div className="col-span-2">
                        <div className="h-6 bg-gray-200 rounded w-28 ml-auto animate-pulse"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Continue Shopping Skeleton */}
          <div className="px-6 py-6 border-t border-gray-200 bg-gray-50">
            <div className="h-5 bg-gray-200 rounded w-40 animate-pulse"></div>
          </div>
        </div>

        {/* Order Summary Skeleton */}
        <div className="lg:w-1/3">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 sticky top-4">
            <div className="px-6 py-5 border-b border-gray-200">
              <div className="h-7 bg-gray-200 rounded w-40 animate-pulse"></div>
            </div>
            
            <div className="px-6 py-5 space-y-4">
              {/* Branch Selection Skeleton */}
              <div>
                <div className="h-4 bg-gray-200 rounded w-32 mb-2 animate-pulse"></div>
                <div className="h-10 bg-gray-200 rounded-lg w-full animate-pulse"></div>
              </div>

              {/* Customer Group Skeleton */}
              <div className="p-4 bg-gray-100 rounded-lg">
                <div className="space-y-2">
                  <div className="h-3 bg-gray-200 rounded w-24 animate-pulse"></div>
                  <div className="h-5 bg-gray-200 rounded w-32 animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-28 animate-pulse"></div>
                </div>
              </div>

              {/* Total Skeleton */}
              <div className="pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between mb-6">
                  <div className="h-6 bg-gray-200 rounded w-24 animate-pulse"></div>
                  <div className="h-8 bg-gray-200 rounded w-32 animate-pulse"></div>
                </div>
                <div className="h-12 bg-gray-200 rounded-lg w-full animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  // Don't render if not authenticated
  if (!isUserLoggedIn) {
    return null;
  }

  return (
    <AvoRedApp>
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
      {fetching && !data?.cartItems ? (
        <CartSkeleton />
      ) : isEmpty ? (
        <div className="flex flex-col items-center justify-center min-h-[500px] bg-white rounded-lg shadow-sm py-12 px-4">
          <svg className="w-24 h-24 text-gray-300 mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
          </svg>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Giỏ hàng của bạn đang trống</h2>
          <p className="text-gray-600 mb-8 text-center max-w-md">Hãy thêm sản phẩm vào giỏ hàng để tiếp tục mua sắm</p>
          <Link 
            to="/" 
            className="inline-flex items-center px-6 py-3 bg-primary-500 text-white font-semibold rounded-lg hover:bg-primary-600 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Tiếp tục mua sắm
          </Link>
        </div>
      ) : (
        <>
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Giỏ hàng</h1>
          <p className="text-gray-600 mt-1">
            {groupedCartItems.reduce((sum: number, group: any) => sum + (group.items?.length || 0), 0)} sản phẩm trong giỏ hàng
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Cart Items Section */}
          <div className="lg:w-2/3 bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <div className="grid grid-cols-12 gap-4 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                <div className="col-span-5">Sản phẩm</div>
                <div className="col-span-3 text-center">Số lượng</div>
                <div className="col-span-2 text-right">Đơn giá</div>
                <div className="col-span-2 text-right">Thành tiền</div>
              </div>
            </div>

            <div className="divide-y divide-gray-200">
              {groupedCartItems.map((group: any, index: number) => {
                const groupTotal = group.items.reduce((sum: number, cartItem: any) => {
                  const variantPrice = get(cartItem, 'variant.final_price') || get(cartItem, 'variant.price');
                  const productPrice = get(cartItem, 'product.final_price') || get(cartItem, 'product.price');
                  const unitPriceRaw = variantPrice || productPrice || 0;
                  const unitPrice = typeof unitPriceRaw === 'string' ? parseFloat(unitPriceRaw) : Number(unitPriceRaw);
                  const qtyRaw = get(cartItem, 'qty', 1);
                  const qty = typeof qtyRaw === 'string' ? parseFloat(qtyRaw) : Number(qtyRaw);
                  
                  if (!isNaN(unitPrice) && !isNaN(qty) && unitPrice >= 0 && qty >= 0) {
                    const itemTotal = unitPrice * qty;
                    return Math.round((sum + itemTotal) * 100) / 100;
                  }
                  return sum;
                }, 0);
                
                return (
                <div key={group.categoryId} className="py-4 border-l-4 border-primary-500 bg-primary-50/20">
                  <div className="px-6 mb-3">
                    <div className="flex items-center justify-between">
                      <div className="inline-flex items-center px-3 py-1.5 bg-primary-500 text-white rounded-lg text-sm font-semibold">
                        <div className="w-6 h-6 rounded-full bg-white text-primary-500 flex items-center justify-center text-xs font-bold mr-2">
                          {index + 1}
                        </div>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                        {group.categoryName}
                        <span className="ml-2 text-xs opacity-90">(Đơn hàng {index + 1})</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-600">Tổng: </span>
                        <span className="font-bold text-primary-600">{formatPrice(groupTotal)} đ</span>
                      </div>
                    </div>
                  </div>
                  
                  {group.items.map((cartItem:any) => {
                    const variantPrice = get(cartItem, 'variant.final_price') || get(cartItem, 'variant.price');
                    const productPrice = get(cartItem, 'product.final_price') || get(cartItem, 'product.price');
                    const unitPriceRaw = variantPrice || productPrice || 0;
                    const unitPrice = typeof unitPriceRaw === 'string' ? parseFloat(unitPriceRaw) : Number(unitPriceRaw);
                    const qtyRaw = get(cartItem, 'qty', 1);
                    const qty = typeof qtyRaw === 'string' ? parseFloat(qtyRaw) : Number(qtyRaw);
                    const keyId = `${get(cartItem, 'product.id')}-${get(cartItem, 'variant.id', 'basic')}`;
                    
                    return (
                      <div key={keyId} className="px-6 py-4 hover:bg-gray-50 transition-colors duration-150">
                        <div className="grid grid-cols-12 gap-4 items-center">
                          {/* Product Info */}
                          <div className="col-span-5 flex items-start space-x-4">
                            <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden border border-gray-200 bg-gray-100">
                              <ImageWithFallback 
                                className="w-full h-full object-cover" 
                                src={get(cartItem, 'product.main_image_url')} 
                                alt={get(cartItem, 'product.name')} 
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <Link 
                                to={`/product/${get(cartItem, 'product.slug')}`}
                                className="font-semibold text-gray-900 mb-1 line-clamp-2 hover:text-primary-600 transition-colors block"
                              >
                                {get(cartItem, 'product.name')}
                              </Link>
                              {get(cartItem, 'variant.id') && (
                                <p className="text-xs text-gray-500 mb-2">
                                  <span className="font-medium">Biến thể:</span> {get(cartItem, 'variant.name')} 
                                  {get(cartItem, 'variant.sku') && <span className="ml-1">({get(cartItem, 'variant.sku')})</span>}
                                </p>
                              )}
                              <button
                                type="button"
                                onClick={() => handleRemove(get(cartItem, 'product.id'), get(cartItem, 'variant.id'))}
                                className="inline-flex items-center text-sm text-red-600 hover:text-red-700 font-medium transition-colors"
                              >
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Xóa
                              </button>
                            </div>
                          </div>

                          {/* Quantity */}
                          <div className="col-span-3">
                            <div className="flex items-center justify-center space-x-2">
                              <input
                                name="qty"
                                type="number"
                                min="1"
                                className="w-20 border border-gray-300 text-center rounded-lg px-2 py-2 text-gray-900 focus:z-10 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 text-sm transition-all"
                                value={qtyDraft[get(cartItem, 'product.slug')] ?? qty}
                                onChange={(e) => handleQtyChange(get(cartItem, 'product.slug'), e.target.value, get(cartItem, 'product.min_order_qty') || 1)}
                              />
                            </div>
                          </div>

                          {/* Unit Price */}
                          <div className="col-span-2 text-right">
                            <span className="font-semibold text-gray-900">{formatPrice(unitPrice)} đ</span>
                          </div>

                          {/* Total Price */}
                          <div className="col-span-2 text-right">
                            {(() => {
                              const itemTotalRaw = (!isNaN(unitPrice) && !isNaN(qty) && unitPrice >= 0 && qty >= 0) 
                                ? (unitPrice * qty) 
                                : 0;
                              const itemTotal = Math.round(itemTotalRaw * 100) / 100;
                              return <span className="font-bold text-lg text-gray-900">{formatPrice(itemTotal)} đ</span>;
                            })()}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
              })}
            </div>

            <div className="px-6 py-6 border-t border-gray-200 bg-gray-50">
              <Link 
                to="/" 
                className="inline-flex items-center text-primary-600 hover:text-primary-700 font-semibold transition-colors"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Tiếp tục mua sắm
              </Link>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:w-1/3">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 sticky top-4">
              <div className="px-6 py-5 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900">Tóm tắt đơn hàng</h2>
              </div>
              
              <div className="px-6 py-5 space-y-4">
                {/* Branch Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Chi nhánh nhận hàng
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all bg-white"
                    value={selectedBranchId ?? ''}
                    onChange={(e) => handleSelectBranch(e.target.value)}
                  >
                    <option value="">Chọn chi nhánh</option>
                    {branches.map((b: any) => (
                      <option key={b.id} value={b.id}>{b.name} - {b.address}</option>
                    ))}
                  </select>
                </div>

                {/* Customer Group Info */}
                {customerGroup && (
                  <div className="p-4 bg-gradient-to-r from-primary-50 to-blue-50 rounded-lg border border-primary-100">
                    <div className="flex items-start">
                      <svg className="w-5 h-5 text-primary-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="flex-1">
                        <p className="text-xs text-gray-600 mb-1">Khách hàng</p>
                        <p className="font-semibold text-gray-900">{customerGroup.name}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Total */}
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-lg font-semibold text-gray-900">Tổng tiền:</span>
                    <span className="text-2xl font-bold text-primary-600">{formatPrice(cartTotal)} đ</span>
                  </div>
                  
                  <button 
                    onClick={() => {
                      const branchExists = branches.some((b: any) => b.id === selectedBranchId);
                      if (!branchExists || !selectedBranchId) {
                        alert('Vui lòng chọn chi nhánh trước khi đặt hàng');
                        return;
                      }
                      setShowOrderModal(true);
                    }}
                    className="w-full bg-gradient-to-r from-primary-500 to-primary-600 text-white font-semibold py-3.5 px-6 rounded-lg hover:from-primary-600 hover:to-primary-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                  >
                    <span className="flex items-center justify-center">
                      Đặt hàng
                      <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        </>
      )}

      {/* Order Confirmation Modal */}
      {showOrderModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
              onClick={() => setShowOrderModal(false)}
            ></div>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-bold text-gray-900" id="modal-title">
                    Xác nhận đặt hàng
                  </h3>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                    onClick={() => setShowOrderModal(false)}
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Customer Group Information */}
                  {customerGroup && (
                    <div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-lg p-4 border-2 border-primary-200">
                      <div className="flex items-center">
                        <svg className="w-6 h-6 text-primary-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <div className="flex-1">
                          <p className="text-xs text-gray-600 mb-1">Khách hàng</p>
                          <p className="text-lg font-bold text-primary-700">{customerGroup.name}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Customer Information */}
                  {customer && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Thông tin người dùng</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-600">Tên:</span>
                          <span className="ml-2 font-medium text-gray-900">{customer.name || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Email:</span>
                          <span className="ml-2 font-medium text-gray-900">{customer.email || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Số điện thoại:</span>
                          <span className="ml-2 font-medium text-gray-900">{customer.phone || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Branch Information */}
                  {selectedBranch && (
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Chi nhánh nhận hàng</h4>
                      <div className="text-sm">
                        <div className="font-medium text-gray-900">{selectedBranch.name}</div>
                        <div className="text-gray-600">{selectedBranch.address}</div>
                      </div>
                    </div>
                  )}

                  {/* Products by Category */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-gray-700">Sản phẩm đặt hàng (theo danh mục)</h4>
                      <div className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-semibold">
                        {groupedCartItems.length} {groupedCartItems.length === 1 ? 'đơn hàng' : 'đơn hàng'}
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">
                      Mỗi danh mục sẽ được tạo thành một đơn hàng riêng biệt
                    </p>
                    <div className="space-y-4">
                      {groupedCartItems.map((group: any, index: number) => {
                        const groupTotal = group.items.reduce((sum: number, cartItem: any) => {
                          const variantPrice = get(cartItem, 'variant.final_price') || get(cartItem, 'variant.price');
                          const productPrice = get(cartItem, 'product.final_price') || get(cartItem, 'product.price');
                          const unitPriceRaw = variantPrice || productPrice || 0;
                          const unitPrice = typeof unitPriceRaw === 'string' ? parseFloat(unitPriceRaw) : Number(unitPriceRaw);
                          const qtyRaw = get(cartItem, 'qty', 1);
                          const qty = typeof qtyRaw === 'string' ? parseFloat(qtyRaw) : Number(qtyRaw);
                          
                          if (!isNaN(unitPrice) && !isNaN(qty) && unitPrice >= 0 && qty >= 0) {
                            const itemTotal = unitPrice * qty;
                            return Math.round((sum + itemTotal) * 100) / 100;
                          }
                          return sum;
                        }, 0);

                        return (
                          <div key={group.categoryId} className="border-2 border-primary-200 rounded-lg p-4 bg-primary-50/30">
                            <div className="flex items-center mb-3">
                              <div className="flex items-center flex-1">
                                <div className="w-8 h-8 rounded-full bg-primary-500 text-white flex items-center justify-center text-sm font-bold mr-3">
                                  {index + 1}
                                </div>
                                <div>
                                  <h5 className="font-semibold text-gray-900">{group.categoryName}</h5>
                                  <p className="text-xs text-gray-500">Đơn hàng {index + 1}/{groupedCartItems.length}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-xs text-gray-500 mb-1">Tổng đơn này:</div>
                                <span className="font-bold text-primary-600">
                                  {formatPrice(groupTotal)} đ
                                </span>
                              </div>
                            </div>
                            <div className="space-y-2">
                              {group.items.map((cartItem: any) => {
                                const variantPrice = get(cartItem, 'variant.final_price') || get(cartItem, 'variant.price');
                                const productPrice = get(cartItem, 'product.final_price') || get(cartItem, 'product.price');
                                const unitPriceRaw = variantPrice || productPrice || 0;
                                const unitPrice = typeof unitPriceRaw === 'string' ? parseFloat(unitPriceRaw) : Number(unitPriceRaw);
                                const qtyRaw = get(cartItem, 'qty', 1);
                                const qty = typeof qtyRaw === 'string' ? parseFloat(qtyRaw) : Number(qtyRaw);
                                const itemTotalRaw = (!isNaN(unitPrice) && !isNaN(qty) && unitPrice >= 0 && qty >= 0) 
                                  ? (unitPrice * qty) 
                                  : 0;
                                const itemTotal = Math.round(itemTotalRaw * 100) / 100;

                                return (
                                  <div key={`${get(cartItem, 'product.id')}-${get(cartItem, 'variant.id', 'basic')}`} className="flex items-center justify-between text-sm py-2 border-b border-gray-100 last:border-0">
                                    <div className="flex-1">
                                      <Link 
                                        to={`/product/${get(cartItem, 'product.slug')}`}
                                        className="font-medium text-gray-900 hover:text-primary-600 transition-colors"
                                      >
                                        {get(cartItem, 'product.name')}
                                      </Link>
                                      {get(cartItem, 'variant.id') && (
                                        <div className="text-gray-500 text-xs">
                                          Biến thể: {get(cartItem, 'variant.name')}
                                        </div>
                                      )}
                                      <div className="text-gray-500 text-xs">
                                        SL: {qty} × {formatPrice(unitPrice)} đ
                                      </div>
                                    </div>
                                    <div className="font-semibold text-gray-900 ml-4">
                                      {formatPrice(itemTotal)} đ
                                    </div>
                                  </div>
                                );
                              })}
                            </div>

                            {/* Order button for each category */}
                            <div className="mt-4 pt-4 border-t border-primary-200">
                              {placedOrders[group.categoryId] ? (
                                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                                  <div className="flex items-center">
                                    <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    <div>
                                      <div className="text-sm font-semibold text-green-800">Đã đặt hàng</div>
                                      <div className="text-xs text-green-600">
                                        Mã đơn: {placedOrders[group.categoryId].track_code || placedOrders[group.categoryId].id}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handlePlaceSingleOrder(group.categoryId)}
                                  disabled={!selectedBranchId || isLoading}
                                  className="w-full inline-flex items-center justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
                                >
                                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  Đặt hàng cho {group.categoryName}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Total */}
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-semibold text-gray-900">Tổng tiền:</span>
                      <span className="text-2xl font-bold text-primary-600">{formatPrice(cartTotal)} đ</span>
                    </div>
                  </div>
                  {/* Ngày giao hàng dự kiến */}
                  {maxDeliveryDate > 0 && (
                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-gray-700">Ngày giao hàng dự kiến (kể từ khi đặt hàng):</span>
                        <span className="text-lg font-bold text-primary-600">{maxDeliveryDate} ngày</span>
                        
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Modal actions */}
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse sm:gap-3">
                {groupedCartItems.some((g: any) => !placedOrders[g.categoryId]) && (
                  <button
                    type="button"
                    onClick={handlePlaceAllOrders}
                    disabled={!selectedBranchId || isLoading}
                    className="w-full inline-flex items-center justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-gradient-to-r from-primary-600 to-primary-700 text-base font-medium text-white hover:from-primary-700 hover:to-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-gray-300 disabled:cursor-not-allowed sm:w-auto sm:text-sm"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Đặt tất cả đơn hàng
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setShowOrderModal(false);
                    setPlacedOrders({});
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  {groupedCartItems.every((g: any) => placedOrders[g.categoryId]) ? 'Đóng' : 'Hủy'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    <Toast
      message={toast.message}
      type={toast.type}
      isVisible={toast.isVisible}
      onClose={handleCloseToast}
    />
    </AvoRedApp>
  )
}
